import { useState, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  useClockConnectData,
  useMissingPunches,
  useRouteHandler,
  usePostPunchRequest,
  useFinalizePunchResult,
  resolveNeededClockPrompts,
  buildPromptRoutePayload,
  CLOCK_PROMPTS_ROUTE,
} from './useWebclock';
import { useTimeWatcherContext } from '../TimeWatcherProvider';
import { useComponentStateContext } from 'swipeclock-frontend';
import { hasValue } from 'swipeclock-frontend';
import { useBuildLoginURL } from './useLoginUser';
import { useUser } from 'store/userSlice';
import { useWhiteLabelContext } from 'providers/WhiteLabelProvider';
import { useBootstrapAuth } from 'components/BootstrapAuthContext';
import { useIsMobileMode, useIsOfflineShellMode } from './useMobileMode';
import { useOfflinePunchQueue } from './useOfflinePunchQueue';
import {
  isAuthError,
  isBusinessError,
  isNetworkError,
  isTransportError,
} from '../utils/isNetworkError';
import { getGeoLocation, readStoredGeoResult } from '../utils/geoLocation';
import { generateId } from '../utils/generateId';
import {
  routeAfterOfflineSave,
  routeAfterOfflineSaveFailure,
} from '../utils/offlinePunchRouting';
import { awaitWithPunchWarning } from '../utils/awaitWithPunchWarning';
import { PUNCH_TIMEOUT_WARNING_MESSAGE } from '../constants/punchSubmitTimeoutConstants';

const OFFLINE_CLOCK_DATA_UNAVAILABLE_MESSAGE =
  'Clock data is not available offline. Connect once while online, then try again.';

/**
 * Calculate minute difference between current time and a schedule time
 * Similar to legacy getMinuteDifference function
 */
function getMinuteDifference(scheduleTime) {
  if (!scheduleTime) return 0;

  const now = new Date();
  const schedule = new Date();

  // If scheduleTime is in HH:MM:SS format, set it for today
  if (typeof scheduleTime === 'string' && scheduleTime.includes(':')) {
    const [hours, minutes, seconds = 0] = scheduleTime.split(':').map(Number);
    schedule.setHours(hours, minutes, seconds, 0);
  } else {
    // If it's a timestamp or Date object
    schedule.setTime(new Date(scheduleTime).getTime());
  }

  return Math.abs(Math.round((now - schedule) / (1000 * 60)));
}

/**
 * Maps technical errors to user-friendly error messages and routing info
 * Based on legacy Angular screenName handling
 */
function getErrorRouting(error, punchData = {}) {
  const errorMessage = error?.message || '';
  const errorString = errorMessage.toLowerCase();

  // Extract screenName from error or punchData if available
  const screenName = error?.screenName || punchData?.screenName || '';

  // Handle specific business logic failures based on screenName
  if (screenName) {
    switch (screenName) {
      case 'LOCKOUT_UNSCHEDULED':
        return {
          route: '/clock/web/pop-ups/punch/alert/failure',
          title: "You're not Scheduled!",
          message:
            error?.reason || 'You are not scheduled to work at this time.',
          buttonText: 'Got It',
        };

      case 'LOCKOUT_EARLY':
        return {
          route: '/clock/web/pop-ups/punch/alert/failure',
          title: "You're early!",
          message:
            error?.reason ||
            'Your next shift starts later. You can clock in at the scheduled time.',
          buttonText: "I'll be back",
        };

      case 'INMEAL_EARLY':
        return {
          route: '/clock/web/pop-ups/punch/alert/failure',
          title: "You're early!",
          message:
            (error?.reason || 'You cannot clock in yet.') +
            (error?.time
              ? ` Current time: ${new Date(
                  error.time * 1000
                ).toLocaleTimeString()}.`
              : ''),
          buttonText: "I'll be back",
        };

      // Warning conditions - Legacy-style messages
      case 'EARLYIN_SUCCESS':
        return {
          route: '/clock/web/pop-ups/punch/alert/warning',
          title: "You're early!",
          message: `Hi ${
            error?.employeeName || 'there'
          }, you are ${getMinuteDifference(
            error?.scheduleStart
          )} minutes early for your shift`,
          buttonText: 'GOT IT',
        };

      case 'EARLYOUT_SUCCESS':
        return {
          route: '/clock/web/pop-ups/punch/alert/warning',
          title: "You're early!",
          message: `Hi ${
            error?.employeeName || 'there'
          }, you are ${getMinuteDifference(
            error?.scheduleEnd
          )} minutes early leaving your shift`,
          buttonText: 'GOT IT',
        };

      case 'LATEIN_SUCCESS':
        return {
          route: '/clock/web/pop-ups/punch/alert/warning',
          title: "You're late!",
          message: `Hi ${
            error?.employeeName || 'there'
          }, you are ${getMinuteDifference(
            error?.scheduleStart
          )} minutes late for your shift`,
          buttonText: 'GOT IT',
        };

      case 'LATEOUT_SUCCESS':
        return {
          route: '/clock/web/pop-ups/punch/alert/warning',
          title: "You're late!",
          message: `Hi ${
            error?.employeeName || 'there'
          }, you are ${getMinuteDifference(
            error?.scheduleEnd
          )} minutes late leaving your shift`,
          buttonText: 'GOT IT',
        };

      default:
        // If screenName is present but not recognized, fall through
        break;
    }
  }

  // Handle technical errors (network, server, etc.)
  if (
    errorString.includes('network') ||
    errorString.includes('fetch') ||
    errorString.includes('connection')
  ) {
    return {
      route: '/clock/web/pop-ups/punch/alert/failure',
      message:
        'Unable to connect to the server. Please check your internet connection and try again.',
      buttonText: 'Try Again',
    };
  }

  if (errorString.includes('timeout') || errorString.includes('timed out')) {
    return {
      route: '/clock/web/pop-ups/punch/alert/failure',
      message: 'The request took too long to complete. Please try again.',
      buttonText: 'Try Again',
    };
  }

  if (
    errorString.includes('unauthorized') ||
    errorString.includes('403') ||
    errorString.includes('401')
  ) {
    return {
      route: '/clock/web/pop-ups/punch/alert/failure',
      message: 'Your session has expired. Please log in again.',
      buttonText: 'Log In',
    };
  }

  if (
    errorString.includes('500') ||
    errorString.includes('server error') ||
    errorString.includes('internal')
  ) {
    return {
      route: '/clock/web/pop-ups/punch/alert/failure',
      message:
        'Server is temporarily unavailable. Please try again in a few moments.',
      buttonText: 'Try Again',
    };
  }

  if (errorString.includes('punch') || errorString.includes('clock')) {
    return {
      route: '/clock/web/pop-ups/punch/alert/failure',
      message:
        'Unable to process your punch. Please try again or contact support if the problem persists.',
      buttonText: 'Try Again',
    };
  }

  if (errorString.includes('validation') || errorString.includes('invalid')) {
    return {
      route: '/clock/web/pop-ups/punch/alert/failure',
      message: 'Invalid punch data. Please refresh the page and try again.',
      buttonText: 'Refresh',
    };
  }

  // Generic fallback
  return {
    route: '/clock/web/pop-ups/punch/alert/failure',
    message:
      'Something went wrong with your punch. Please try again or contact support if the problem continues.',
    buttonText: 'Try Again',
  };
}

const shouldQueueOfflinePunch = ({ error, isOffline }) => {
  if (error?.screenName) {
    return false;
  }

  if (isBusinessError(error)) {
    return false;
  }

  return isOffline || isNetworkError(error);
};

const routeToOfflineClockDataFailure = ({
  setRouteData,
  history,
  punch,
  requestType,
  message = OFFLINE_CLOCK_DATA_UNAVAILABLE_MESSAGE,
}) => {
  const route = routeAfterOfflineSaveFailure({
    setRouteData,
    punch,
    requestType,
    message,
  });
  history.replace(route);
};

const tryRouteToOfflineClockPrompts = async ({
  connectData,
  punch,
  requestType,
  setRouteData,
  history,
}) => {
  if (!connectData) {
    return false;
  }

  const neededPrompts = resolveNeededClockPrompts(
    connectData.clockPrompts,
    punch
  );

  if (neededPrompts.length === 0) {
    return false;
  }

  const routePayload = buildPromptRoutePayload({
    clockPrompts: neededPrompts,
    punch,
    overridePunch: true,
  });

  setRouteData({
    punch,
    punchType: punch,
    requestType,
    punchSessionId: generateId(),
    ...routePayload,
  });

  history.replace(CLOCK_PROMPTS_ROUTE);
  return true;
};

const navigateToPunchTimeoutWarning = ({
  setRouteData,
  history,
  punch,
  requestType,
}) => {
  setRouteData({
    punch,
    punchType: punch,
    requestType,
    warning: PUNCH_TIMEOUT_WARNING_MESSAGE,
  });
  history.replace('/clock/web/pop-ups/punch/alert/warning');
};

/**
 * ButtonPad Hook - Combines data fetching, business logic, and state management
 * Simplified single hook that orchestrates all ButtonPad functionality
 */
export function useButtonPad() {
  const history = useHistory();
  const user = useUser();
  const buttonPadRef = useRef({ hasSet: false, count: 4 });

  // Core data hooks
  const { data, isLoading, isFetching } = useClockConnectData();
  const { missingPunches, setMissingPunches } = useMissingPunches();
  const { pauseTimer, startTimer } = useTimeWatcherContext();
  // We specifically use the actual values for context and not the useLoading hook to reduce complexity.
  // See how buttonLoading and setButtonLoading are used below with value to keep track of what things are loading.
  const { loading: buttonLoading, setLoading: setButtonLoading } =
    useComponentStateContext();
  const { state: whiteLabelState } = useWhiteLabelContext();

  // Local state
  // This is for the component itself and not individual buttons. Important!
  const [buttonPadLoading, setButtonPadLoading] = useState(false);
  const punchInFlightRef = useRef(false);

  // Business logic hooks
  const { handlePostPunchRequest, requestType } = usePostPunchRequest();
  const { handleRoute, setRouteData } = useRouteHandler(requestType);
  const { handleFinalizePunchResult } = useFinalizePunchResult(requestType);
  const { isMobileMode } = useIsMobileMode();
  const { isOfflineShellMode } = useIsOfflineShellMode();
  const { enqueuePunch, isOffline, reportApiUnreachable } =
    useOfflinePunchQueue();
  const { reportExpiredSession } = useBootstrapAuth();

  // build login url for ess
  const buildLoginUrl = useBuildLoginURL();

  // Button state with skeleton count management
  const buttonState = useMemo(() => {
    const buttonList = data?.displayButtons || [];
    const disabledButtons = data?.disabledButtonMap || {};

    // Update skeleton count when buttons are loaded
    if (!buttonPadRef.current.hasSet && hasValue(buttonList)) {
      buttonPadRef.current.count = buttonList.length;
      buttonPadRef.current.hasSet = true;
    }

    return {
      buttonList,
      disabledButtons,
      missingPunches,
      skeletonCount: buttonPadRef.current.count,
    };
  }, [data, missingPunches, isMobileMode, isOffline]); // data is stable and missingPunches is primitive

  // Main button click handler
  const handleButtonClick = useCallback(
    (value) => async () => {
      if (punchInFlightRef.current) {
        return;
      }
      punchInFlightRef.current = true;

      let punchResult = null;
      let routeData = null;
      const captureGeoResult = (priorResult) => {
        const stored = readStoredGeoResult(priorResult);
        if (stored) {
          return Promise.resolve(stored);
        }

        return getGeoLocation(isMobileMode, data?.geoLocationRequired);
      };

      try {
        setButtonPadLoading(true);
        setButtonLoading(value)(true);

        // Pause the timer
        pauseTimer();

        // Business logic: Process punch request
        const punch = buttonState.buttonList.find(
          (button) => button.value === value
        )?.punch;

        if (!punch && value === 'ESS') {
          const loginUrl = buildLoginUrl({
            token: user.token,
            employeePortalLogin: true,
            altURL: whiteLabelState.urlESS,
            excludeJWTFromParams: true,
          });
          window.location.assign(loginUrl);
          return;
        }

        if (isOfflineShellMode && isOffline) {
          if (!data) {
            routeToOfflineClockDataFailure({
              setRouteData,
              history,
              punch,
              requestType,
            });
            startTimer();
            return;
          }

          const routedToPrompts = await tryRouteToOfflineClockPrompts({
            connectData: data,
            punch,
            requestType,
            setRouteData,
            history,
          });

          if (routedToPrompts) {
            if (missingPunches) {
              setMissingPunches(false);
            }
            startTimer();
            return;
          }

          try {
            const geoResult = await captureGeoResult();
            await enqueuePunch({
              source: 'dashboard',
              punch,
              requestType,
              overridePunch: true,
              ...geoResult,
            });

            if (missingPunches) {
              setMissingPunches(false);
            }

            const route = routeAfterOfflineSave({
              setRouteData,
              punch,
              requestType,
            });
            history.replace(route);
          } catch (saveError) {
            const route = routeAfterOfflineSaveFailure({
              setRouteData,
              punch,
              requestType,
              message: saveError?.message,
            });
            history.replace(route);
          }

          startTimer();
          return;
        }

        const punchRequest = handlePostPunchRequest({
          overridePunch: missingPunches ?? false,
          punch,
        });
        punchResult = isOfflineShellMode
          ? await awaitWithPunchWarning(punchRequest, () =>
              navigateToPunchTimeoutWarning({
                setRouteData,
                history,
                punch,
                requestType,
              })
            )
          : await punchRequest;

        const { data: routePayload, route } = await handleRoute(
          punch,
          punchResult
        );
        routeData = routePayload;

        setRouteData({
          punchResult,
          punchType: punch,
          requestType,
          route,
          ...routeData,
          ...(route === CLOCK_PROMPTS_ROUTE &&
            isMobileMode && {
              punchSessionId: generateId(),
            }),
        });

        // Handle success route finalization
        if (route === '/clock/web/pop-ups/punch/alert/success') {
          const finalizePunch = handleFinalizePunchResult(punch, routeData);
          if (isOfflineShellMode) {
            await awaitWithPunchWarning(finalizePunch, () =>
              navigateToPunchTimeoutWarning({
                setRouteData,
                history,
                punch,
                requestType,
              })
            );
          } else {
            await finalizePunch;
          }
        }

        if (missingPunches) {
          setMissingPunches(false);
        }

        history.replace(route);
      } catch (e) {
        if (isMobileMode && isAuthError(e)) {
          reportExpiredSession();
          startTimer();
          history.replace('/clock/web');
          return;
        }

        if (isOfflineShellMode && isTransportError(e)) {
          reportApiUnreachable();
        }

        if (
          isOfflineShellMode &&
          shouldQueueOfflinePunch({ error: e, isOffline })
        ) {
          const punch =
            buttonState.buttonList.find((button) => button.value === value)
              ?.punch || 'Unknown';

          const hasPromptRouteData =
            hasValue(routeData?.clockPrompts) &&
            routeData.clockPrompts.length > 0;

          if (!hasPromptRouteData) {
            const routedToPrompts = await tryRouteToOfflineClockPrompts({
              connectData: data,
              punch,
              requestType,
              setRouteData,
              history,
            });

            if (routedToPrompts) {
              if (missingPunches) {
                setMissingPunches(false);
              }
              startTimer();
              return;
            }
          }

          try {
            const geoResult = await captureGeoResult(e);
            await enqueuePunch({
              source: 'dashboard',
              punch,
              requestType,
              overridePunch: true,
              punchResult: punchResult || undefined,
              clockPrompts: routeData?.clockPrompts,
              expectedPunches: routeData?.expectedPunches,
              ...geoResult,
            });

            if (missingPunches) {
              setMissingPunches(false);
            }

            const errorRouting = getErrorRouting(e, punchResult || {});
            const route = routeAfterOfflineSave({
              setRouteData,
              punch,
              requestType,
              punchResult: punchResult || undefined,
              errorRouting,
            });
            history.replace(route);
          } catch (saveError) {
            const route = routeAfterOfflineSaveFailure({
              setRouteData,
              punch,
              requestType,
              message: saveError?.message,
            });
            history.replace(route);
          }

          startTimer();
          return;
        }

        // Business logic: Handle errors
        startTimer();
        console.error('ButtonPad punch error:', e);

        // Pass punchResult to access screenName if available
        const errorRouting = getErrorRouting(e, punchResult || {});

        setRouteData({
          punch:
            buttonState.buttonList.find((button) => button.value === value)
              ?.punch || 'Unknown',
          error: errorRouting.message,
          title: errorRouting.title,
          buttonText: errorRouting.buttonText,
          requestType: 'punch-error',
          ...(errorRouting.route.includes('warning') && {
            warning: errorRouting.message,
          }),
        });

        history.replace(errorRouting.route);
      } finally {
        punchInFlightRef.current = false;
        setButtonPadLoading(false);
        setButtonLoading(value)(false);
      }
    },
    [
      buttonState,
      setButtonLoading,
      pauseTimer,
      startTimer,
      handlePostPunchRequest,
      handleRoute,
      handleFinalizePunchResult,
      setRouteData,
      history,
      isMobileMode,
      isOfflineShellMode,
      isOffline,
      enqueuePunch,
      reportApiUnreachable,
      missingPunches,
      setMissingPunches,
      buildLoginUrl,
      user.token,
      data,
      requestType,
      reportExpiredSession,
      whiteLabelState.urlESS,
    ]
  );

  // Simple return - no over-memoization
  return {
    buttonState,
    isLoading,
    isFetching,
    buttonPadLoading,
    buttonLoading,

    handleButtonClick,
  };
}
